// src/components/admin/EditMessageTemplate.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Tag } from 'lucide-react';
import { getTemplateById, createTemplate, updateTemplate, MessageTemplate } from '../../services/messageTemplateService';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';

const availableTags = [
  { name: 'Nome do Contratante', value: '{{contratante}}' },
  { name: 'Nome do Aniversariante', value: '{{nomeAniversariante}}' },
  { name: 'Nova Idade', value: '{{novaIdade}}' },
  { name: 'Data do Aniversário', value: '{{proximoAniversario}}' },
  { name: 'Dias Restantes', value: '{{diasRestantes}}' },
];

const EditMessageTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const isEditing = !!id;

  useEffect(() => {
    if (isEditing) {
      const loadTemplate = async () => {
        try {
          setLoading(true);
          const template = await getTemplateById(id);
          if (template) {
            setName(template.name);
            setContent(template.content);
          } else {
            alert("Modelo não encontrado.");
            navigate('/admin/mensagens');
          }
        } catch (error) {
          console.error("Erro ao carregar modelo:", error);
          navigate('/admin/mensagens');
        } finally {
          setLoading(false);
        }
      };
      loadTemplate();
    } else {
      setLoading(false);
    }
  }, [id, isEditing, navigate]);

  const handleInsertTag = (tag: string) => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const newText = `${text.substring(0, start)} ${tag} ${text.substring(end)}`;
    
    setContent(newText);
    textarea.focus();
    // Move cursor to after the inserted tag
    const newCursorPosition = start + tag.length + 2;
    setTimeout(() => textarea.setSelectionRange(newCursorPosition, newCursorPosition), 0);
  };

  const handleSave = async () => {
    if (!name.trim() || !content.trim()) {
      alert("O nome e o conteúdo do modelo são obrigatórios.");
      return;
    }

    setIsSaving(true);
    try {
      const templateData = { name, content };
      if (isEditing) {
        await updateTemplate(id, templateData);
      } else {
        await createTemplate(templateData);
      }
      alert(`Modelo ${isEditing ? 'atualizado' : 'criado'} com sucesso!`);
      navigate('/admin/mensagens');
    } catch (error) {
      console.error("Erro ao salvar modelo:", error);
      alert("Não foi possível salvar o modelo.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6">
        <div className="mx-2 sm:mx-4 lg:mx-6 mt-2 mb-3">
          <PageTitle
            title={isEditing ? "Editar Modelo" : "Criar Novo Modelo"}
            subtitle="Construa mensagens personalizadas para seus clientes"
          >
            <button
              onClick={() => navigate('/admin/mensagens')}
              className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </button>
          </PageTitle>
        </div>

        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna do Editor */}
          <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-6">
            <div className="space-y-6">
              <div>
                <label htmlFor="template-name" className="block text-sm font-medium text-slate-700 mb-2">
                  Nome do Modelo
                </label>
                <input
                  type="text"
                  id="template-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Ex: Lembrete de Aniversário"
                />
              </div>
              <div>
                <label htmlFor="template-content" className="block text-sm font-medium text-slate-700 mb-2">
                  Conteúdo da Mensagem
                </label>
                <textarea
                  id="template-content"
                  ref={contentRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono"
                  placeholder="Olá {{contratante}}! O aniversário de {{nomeAniversariante}} está chegando..."
                />
              </div>
            </div>
          </div>

          {/* Coluna das Tags */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-teal-600" />
                Tags Dinâmicas
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Clique em uma tag para inseri-la na sua mensagem. Elas serão substituídas pelos dados reais do cliente.
              </p>
              <div className="flex flex-wrap gap-2">
                {availableTags.map(tag => (
                  <button
                    key={tag.value}
                    onClick={() => handleInsertTag(tag.value)}
                    className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full hover:bg-teal-200 transition-colors text-sm font-mono"
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Botão Salvar Fixo */}
        <div className="p-4 mt-4">
           <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-lg font-semibold bg-teal-600 text-white rounded-xl shadow-lg hover:bg-teal-700 disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Salvando...' : 'Salvar Modelo'}
            </button>
        </div>
      </div>
    </PageTransition>
  );
};

export default EditMessageTemplate;
